import React, { useEffect, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { getStyles } from '../../theme/styles';
import { Typography, Card, List, ListItem, ListItemText, Collapse, Box } from '@mui/material';
import { SiteDTO, SitePauseDateDTO, OrderListItem } from '@beable/roster-backend-client-v1';
import adminManagementService from '../../services/AdminManagementService';
import { ArrowRight, ArrowDropDown } from '@mui/icons-material';
import { useCustomerState } from "../../context/CustomerContext";
import rosterBackendService from '../../services/RosterBackendService';
import moment from 'moment';
import { YYYY_MM_DD_HH_mm_ss } from '@beable/mwm-commons-date-time';

import ListItemButton from "@mui/material/ListItemButton";

interface ProvisionSelectProps {
  site: SiteDTO;
}

export default function SiteCard({ site }: ProvisionSelectProps) {
  const theme = useTheme();
  const styles = getStyles(theme);
  const { site: siteState }: any = useCustomerState();

  const [isLoading, setIsLoading] = useState(true);
  const [orderList, setOrderList] = useState<OrderListItem[]>([]);
  const [open, setOpen] = useState<{ [key: string]: boolean }>({});
  const [details, setDetails] = useState(site ? site : siteState);
  const [pauseDates, setPauseDates] = useState<SitePauseDateDTO[]>([]);

  const handleClick = (subOrderId: string) => {
    setOpen({ ...open, [subOrderId]: !open[subOrderId] });
  };

  useEffect(() => {
    const getPauseDates = async () => {
      setIsLoading(true);
      if (site) {
        const response = await rosterBackendService.getSitePauseDatesWithParse(site.id);
        if (response.isOk() && response.value.pauseDates) {
          setPauseDates(response.value.pauseDates);
        } else {
          setPauseDates([]);
        }
      } else {
        setPauseDates([]);
      }
      setIsLoading(false);
    }
    getPauseDates();
  }, [site]);

  useEffect(() => {
    async function getOrderList() {
      if (site) {
        setIsLoading(true);
        const newOrderList = await adminManagementService.getOrderListForSite(site.id);
        if (newOrderList) {
          const currentOrders = (newOrderList as OrderListItem[]).filter(o => new Date(o.endDate) > new Date());
          setOrderList(currentOrders);
        }
      }
    }

    setDetails(site);
    getOrderList();
    setIsLoading(false);
  }, [site, site.id]);

  useEffect(() => {
    setDetails({ ...site, ...siteState });
  }, [site, siteState]);

  return (
    <Card sx={styles.customerCard} variant='outlined'>
      <Typography variant='h6' component='h6' sx={styles.customerCard_caption}>
        Site Details
      </Typography>
      {details && (
        <>
          <Typography variant="body1" sx={styles.customerCard_content}>
            Siteid: {details.id}
          </Typography>
          <Typography variant="body1" sx={styles.customerCard_content}>
            SSO: {details.ssoProviders ? details.ssoProviders : "none"}
          </Typography>
          <Typography variant="body1" sx={styles.customerCard_content}>
            Roster Source: {details.integrationType}
          </Typography>
          <Typography variant="body1" sx={styles.customerCard_content}>
            Roster Mode: {details.integrationMethod}
          </Typography>
          <Typography variant="body1" sx={styles.customerCard_content}>
            Type: {details.type}
          </Typography>
        </>
      )}
      <Typography variant="h6" component="h6" sx={styles.customerCard_caption}>
        Orders
      </Typography>
      <List
        dense={true}
        component="nav"
        aria-labelledby="nested-list-subheader"
        sx={styles.root}
      >
        {!isLoading && orderList && orderList.length > 0 &&
          orderList.map((order) => {
            return (
              <React.Fragment key={order.externalOrderId}>
                <ListItemButton
                  onClick={() => handleClick(order.externalOrderId)}
                  key={order.externalOrderId + '-order'}>
                  {open[order.externalOrderId] ? <ArrowDropDown /> : <ArrowRight />}
                  <ListItemText primary={`${order.externalOrderId}`} />
                </ListItemButton>
                <Collapse in={open[order.externalOrderId]} timeout="auto" unmountOnExit key={order.externalOrderId + '-orderDetails'}>
                  <List component="div" disablePadding sx={styles.listItem}>
                    <ListItem>
                      Dates: {new Date(order.startDate).toLocaleDateString()} - {new Date(order.endDate).toLocaleDateString()}<br />
                      {'Schools: ' + order.schools}<br />
                    </ListItem>
                    {order.orderProducts.map((orderProduct) => {
                      return (
                        <React.Fragment key={orderProduct.id}>
                          <ListItemButton
                            sx={styles.listItemPadding}
                            onClick={() => handleClick(orderProduct.id)}
                            key={orderProduct.id + '-orderproduct'}>
                            {open[orderProduct.id] ? <ArrowDropDown /> : <ArrowRight />}
                            <ListItemText
                              slotProps={{ primary: { sx: styles.listItemText } }}
                              primary={`${orderProduct.productName} : ${orderProduct.schoolYear}`}
                            />
                          </ListItemButton>
                          <Collapse in={open[orderProduct.id]} timeout="auto" unmountOnExit key={orderProduct.id + '-orderDetails'}>
                            <List component="div" disablePadding sx={styles.listItem}>
                              <ListItem >
                                {'Licenses: ' + orderProduct.numLicenses}<br />
                              </ListItem>
                            </List>
                          </Collapse>
                        </React.Fragment>
                      );
                    })}
                  </List>
                </Collapse>
              </React.Fragment>
            );
          })}
        {!isLoading && orderList && orderList.length === 0 &&
          <ListItem>
            <ListItemText primary="No current or future orders found" />
          </ListItem>
        }
      </List>
      <Box>
        <Typography variant="h6" component="h6" sx={styles.customerCard_caption}>
          Pause Dates
        </Typography>
        <List dense={true}>
          {
            !isLoading && (pauseDates.length > 0) ?
              pauseDates.map((item, index) => (
                <ListItem key={index} >
                  <Typography component="span">
                    {moment(item.startDate.toString()).format('yyyy-MM-DD')} - {moment(item.endDate.toString()).format('yyyy-MM-DD')} {item.active ? <Typography component="span" sx={{ color: 'green' }}>Active</Typography> : <Typography component="span" sx={{ color: 'gray' }}>Inactive</Typography>}
                  </Typography>
                </ListItem>
              )) :
              <ListItem>
                <ListItemText primary="No pause dates found" />
              </ListItem>
          }
        </List>
      </Box>
    </Card>
  );
}
